import type {
  MyOpsAlertSettings,
  OpsChannelSetting,
  OpsChannelTestResult,
  OpsGlobalSettings,
  OpsIncidentDetail,
  OpsIncidentKind,
  OpsIncidentListResponse,
  OpsIncidentSeverity,
  OpsIncidentSummary,
  OpsTaskRunRow,
  OpsTaskRunStatus,
  OpsAlertChannelType,
} from '@safetyhub/shared';
import api from './api';

/**
 * 운영 사건 — 서버 `apps/server/src/ops-incidents/ops-incidents.controller.ts`(`admin/ops-incidents`)의 클라이언트.
 * 게이트는 서버와 같은 축이다: 읽기·확인·내 설정 = `monitoring-view` · 전역 설정 쓰기 = `system-settings`.
 *
 * union·응답 형상·라벨의 정본은 shared `features/ops-incidents.ts`다(리뷰 O6 — 종전엔 여기 손사본이었다).
 * 여기는 그것을 다시 내보내고, 화면 전용(색 클래스·쿼리 키·API 호출)만 둔다.
 */
export {
  OPS_CHANNEL_LABELS,
  OPS_KIND_LABELS,
  OPS_SEVERITY_LABELS,
  OPS_SEVERITY_ORDER,
  OPS_TASK_RUN_STATUS_LABELS,
} from '@safetyhub/shared';
export type {
  MyOpsAlertSettings,
  OpsAlertChannelType,
  OpsChannelSetting,
  OpsIncident,
  OpsIncidentDelivery,
  OpsIncidentDetail,
  OpsIncidentKind,
  OpsIncidentSeverity,
  OpsIncidentSummary,
  OpsTaskRunRow,
  OpsTaskRunStatus,
} from '@safetyhub/shared';

export interface ListOpsIncidentsParams {
  status?: 'open' | 'acknowledged' | 'all';
  severity?: OpsIncidentSeverity;
  kind?: OpsIncidentKind;
  sourceKey?: string;
  page?: number;
  limit?: number;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

export const OPS_SEVERITY_CLASSES: Record<OpsIncidentSeverity, string> = {
  INFO: 'bg-gray-100 text-gray-700',
  WARNING: 'bg-yellow-100 text-yellow-800',
  ERROR: 'bg-red-100 text-red-700',
  CRITICAL: 'bg-red-600 text-white',
};

/** 실행 기록 상태 칸의 색 — 「실패 중」은 낡음보다 한 단계 약하게(아직 예정 안에서 다시 시도된다) */
export const OPS_TASK_RUN_STATUS_CLASSES: Record<OpsTaskRunStatus, string> = {
  unrecorded: 'bg-red-100 text-red-700',
  stale: 'bg-red-100 text-red-700',
  failing: 'bg-yellow-100 text-yellow-800',
  ok: 'bg-green-100 text-green-700',
};

export const OPS_INCIDENT_QUERY_KEYS = {
  all: ['admin', 'ops-incidents'] as const,
  summary: ['admin', 'ops-incidents', 'summary'] as const,
  list: (params: ListOpsIncidentsParams) => ['admin', 'ops-incidents', 'list', params] as const,
  detail: (id: string) => ['admin', 'ops-incidents', 'detail', id] as const,
  taskRuns: ['admin', 'ops-incidents', 'task-runs'] as const,
  mySettings: ['admin', 'ops-incidents', 'settings', 'me'] as const,
  globalSettings: ['admin', 'ops-incidents', 'settings', 'global'] as const,
};

export const opsIncidentsApi = {
  async list(params: ListOpsIncidentsParams) {
    const res = await api.get<Envelope<OpsIncidentListResponse>>('/admin/ops-incidents', { params });
    return res.data.data;
  },
  async summary() {
    const res = await api.get<Envelope<OpsIncidentSummary>>('/admin/ops-incidents/summary');
    return res.data.data;
  },
  async taskRuns() {
    const res = await api.get<Envelope<OpsTaskRunRow[]>>('/admin/ops-incidents/task-runs');
    return res.data.data;
  },
  async get(id: string) {
    const res = await api.get<Envelope<OpsIncidentDetail>>(`/admin/ops-incidents/${id}`);
    return res.data.data;
  },
  async acknowledge(id: string, note?: string) {
    const res = await api.post<Envelope<OpsIncidentDetail>>(`/admin/ops-incidents/${id}/acknowledge`, {
      note: note?.trim() || undefined,
    });
    return res.data.data;
  },
  async getMySettings() {
    const res = await api.get<Envelope<MyOpsAlertSettings>>('/admin/ops-incidents/settings/me');
    return res.data.data;
  },
  async updateMySettings(channels: OpsChannelSetting[]) {
    const res = await api.put<Envelope<MyOpsAlertSettings>>('/admin/ops-incidents/settings/me', {
      channels: channels.map((c) => ({
        channel: c.channel,
        isEnabled: c.isEnabled,
        target: c.target,
        minSeverity: c.minSeverity,
      })),
    });
    return res.data.data;
  },
  /** ⛔ 실제 발송이다 — 저장된 설정으로 그 채널 하나에 시험 메시지를 보낸다 */
  async sendTest(channel: OpsAlertChannelType) {
    const res = await api.post<Envelope<OpsChannelTestResult>>(
      '/admin/ops-incidents/settings/me/test',
      { channel },
    );
    return res.data.data;
  },
  async getGlobalSettings() {
    const res = await api.get<Envelope<OpsGlobalSettings>>(
      '/admin/ops-incidents/settings/global',
    );
    return res.data.data;
  },
  async updateGlobalSettings(forwardToSentry: boolean) {
    const res = await api.put<Envelope<OpsGlobalSettings>>(
      '/admin/ops-incidents/settings/global',
      { forwardToSentry },
    );
    return res.data.data;
  },
};
